import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Check, X, Sparkles, Crown, Building2, Coffee, Wine, ChefHat, ArrowRight, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/subscriptions/plans`);
      // Filter out free plan for display
      setPlans(res.data.plans.filter(p => p.id !== 'free'));
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const res = await axios.get(`${API}/api/subscriptions/current`, { withCredentials: true });
      setCurrentPlan(res.data.subscription?.plan);
    } catch (err) {
      console.error("Error fetching subscription:", err);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/login");
      return;
    }

    setProcessingPlan(planId);
    try {
      const res = await axios.post(`${API}/api/payments/create-checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen der Checkout-Session");
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'getraenke': return <Wine className="w-8 h-8" />;
      case 'cafe': return <Coffee className="w-8 h-8" />;
      case 'restaurant': return <ChefHat className="w-8 h-8" />;
      default: return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'getraenke': return { bg: 'from-blue-500 to-blue-600', border: 'border-blue-500', text: 'text-blue-500' };
      case 'cafe': return { bg: 'from-amber-500 to-orange-500', border: 'border-amber-500', text: 'text-amber-500' };
      case 'restaurant': return { bg: 'from-violet-500 to-purple-600', border: 'border-violet-500', text: 'text-violet-500' };
      default: return { bg: 'from-slate-500 to-slate-600', border: 'border-slate-500', text: 'text-slate-500' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4" data-testid="pricing-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-1.5 mb-6">
          <Zap size={16} className="text-violet-400" />
          <span className="text-sm text-violet-300 font-medium">Bis zu 50% Rabatt - Limitiertes Angebot</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Wähle deinen <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-blue-400">Plan</span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          Optimiere deine Gastronomie-Kalkulation mit dem passenden Plan für dein Business
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 lg:gap-8">
        {plans.map((plan, index) => {
          const colors = getPlanColor(plan.id);
          const isPopular = plan.id === 'cafe';
          const isCurrent = currentPlan === plan.id;
          
          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                isPopular ? 'ring-2 ring-amber-500 shadow-xl shadow-amber-500/20' : ''
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center text-sm font-semibold py-2">
                  <Crown size={14} className="inline mr-1" />
                  BELIEBTESTER PLAN
                </div>
              )}

              {/* Card Content */}
              <div className={`bg-slate-800/90 backdrop-blur-sm p-6 lg:p-8 h-full flex flex-col ${isPopular ? 'pt-14' : ''}`}>
                {/* Icon & Name */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center text-white mb-4`}>
                  {getPlanIcon(plan.id)}
                </div>
                
                <h3 className="text-xl font-bold text-white mb-1">{plan.display_name}</h3>
                <p className="text-sm text-slate-400 mb-4">{plan.target_audience}</p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">{plan.price.toFixed(2).replace('.', ',')}€</span>
                    <span className="text-slate-500">/Monat</span>
                  </div>
                  {plan.original_price > plan.price && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-slate-500 line-through">{plan.original_price.toFixed(2).replace('.', ',')}€</span>
                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                        -{plan.discount_percent}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check size={18} className={`${colors.text} shrink-0 mt-0.5`} />
                      <span className="text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={processingPlan === plan.id || isCurrent}
                  className={`w-full py-6 text-base font-semibold transition-all ${
                    isCurrent 
                      ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${colors.bg} hover:opacity-90 text-white`
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {processingPlan === plan.id ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Wird verarbeitet...
                    </span>
                  ) : isCurrent ? (
                    'Aktueller Plan'
                  ) : (
                    <span className="flex items-center gap-2">
                      Jetzt starten
                      <ArrowRight size={18} />
                    </span>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Features Comparison */}
      <div className="max-w-4xl mx-auto mt-16 text-center">
        <h2 className="text-2xl font-bold text-white mb-8">Alle Pläne beinhalten</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['Sichere Daten', 'Regelmäßige Updates', 'Deutscher Support', 'DSGVO-konform'].map((item, i) => (
            <div key={i} className="bg-slate-800/50 rounded-lg p-4 text-center">
              <Check className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
              <span className="text-sm text-slate-300">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Link */}
      <div className="text-center mt-12">
        <p className="text-slate-400">
          Fragen? {' '}
          <button 
            onClick={() => navigate("/login")}
            className="text-violet-400 hover:text-violet-300 underline"
          >
            Kontaktiere uns
          </button>
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
